import * as service from '../../services/communityMonitoring.service'
import { deriveLevel, colorForDomainCode } from '../../enums/communityMonitoring.enum'

const lastScore = (trend) => (trend && trend.length ? trend[trend.length - 1].score : null)
const previousScore = (trend) => (trend && trend.length > 1 ? trend[trend.length - 2].score : null)

const alertError = (commit, error) => {
  commit('baseSnackbar/alert', { text: error.response && error.response.data && error.response.data.message, code: error.response && error.response.status }, { root: true })
}

export const state = {
  communityMonitoringStore: null, // cabecera de la comunidad seleccionada
  autonomyDashboard: null, // respuesta cruda de GET /community/:id/autonomy-dashboard
  interventions: [],
  facilitatorActions: [],
  interventionStore: null,
  catalog: null, // respuesta cruda de GET /autonomy/catalog
  monthlyChecklist: null, // respuesta cruda de GET /autonomy-checklist/:id (type MONTHLY), null si es nueva
  autoIndicators: [],
  biannualChecklist: null, // respuesta cruda de GET /autonomy-checklist/:id (type BIANNUAL), null si es nueva
  teamActivity: null
}

export const mutations = {
  'SET_COMMUNITY_MONITORING' (state, data) {
    state.communityMonitoringStore = data
  },
  'SET_AUTONOMY_DASHBOARD' (state, data) {
    state.autonomyDashboard = data
  },
  'SET_INTERVENTIONS' (state, data) {
    state.interventions = data
  },
  'SET_FACILITATOR_ACTIONS' (state, data) {
    state.facilitatorActions = data
  },
  'SET_INTERVENTION' (state, data) {
    state.interventionStore = data
  },
  'SET_AUTO_INDICATORS' (state, data) {
    state.autoIndicators = data
  },
  'SET_CATALOG' (state, data) {
    state.catalog = data
  },
  'SET_MONTHLY_CHECKLIST' (state, data) {
    state.monthlyChecklist = data
  },
  'SET_BIANNUAL_CHECKLIST' (state, data) {
    state.biannualChecklist = data
  },
  'SET_TEAM_ACTIVITY' (state, data) {
    state.teamActivity = data
  }
}

export const getters = {
  overallScore: (state) => lastScore(state.autonomyDashboard && state.autonomyDashboard.overall.trend),
  overallPreviousScore: (state) => previousScore(state.autonomyDashboard && state.autonomyDashboard.overall.trend),
  overallDelta: (state, getters) => {
    if (getters.overallScore === null || getters.overallPreviousScore === null) return null
    return Number((getters.overallScore - getters.overallPreviousScore).toFixed(2))
  },
  overallLevel: (state, getters) => deriveLevel(getters.overallScore),

  domains: (state) => {
    const domains = (state.autonomyDashboard && state.autonomyDashboard.domains) || []
    return domains.map((domain) => {
      const score = lastScore(domain.trend)
      return { ...domain, score, level: deriveLevel(score), color: colorForDomainCode(domain.code) }
    })
  },
  primaryConstraintDomain: (state, getters) => {
    const scored = getters.domains.filter((d) => d.score !== null)
    return scored.length ? scored.reduce((min, d) => (d.score < min.score ? d : min)) : null
  },
  strengthDomain: (state, getters) => {
    const scored = getters.domains.filter((d) => d.score !== null)
    return scored.length ? scored.reduce((max, d) => (d.score > max.score ? d : max)) : null
  },

  communityCenterName: (state, getters, rootState) => {
    const header = state.communityMonitoringStore
    const center = header && rootState.center.centers.find((c) => c.id === header.centerId)
    return center ? center.name : ''
  },

  lastMonthlyChecklist: (state) => (state.autonomyDashboard && state.autonomyDashboard.lastMonthlyChecklist) || null,
  lastBiannualChecklist: (state) => (state.autonomyDashboard && state.autonomyDashboard.lastBiannualChecklist) || null,
  comments: (state) => (state.autonomyDashboard && state.autonomyDashboard.comments) || []
}

export const actions = {

  getCommunityHeader: async ({ commit, dispatch, state, rootState }, id) => {
    if (state.communityMonitoringStore && state.communityMonitoringStore.id === id) return true
    return await service.getCommunityHeader(id)
      .then(async (data) => {
        commit('SET_COMMUNITY_MONITORING', data)
        if (!rootState.center.centers.length) await dispatch('center/getCentersByFoundationId', null, { root: true })
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  getAutonomyDashboard: async ({ commit }, { id, months, from, to } = {}) => {
    return await service.getAutonomyDashboard(id, { months, from, to })
      .then((data) => {
        commit('SET_AUTONOMY_DASHBOARD', data)
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  getInterventions: async ({ commit }, { id, kind, from, to }) => {
    const params = {
      kind,
      from,
      to,
      includeDisabled: false,
      itemsPerPage: 100
    }
    return await service.getActivities(id, params)
      .then((data) => {
        commit(kind === 'ENABLER' ? 'SET_FACILITATOR_ACTIONS' : 'SET_INTERVENTIONS', data.items)
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  getInterventionById: async ({ commit }, { id, interventionId }) => {
    return await service.getActivity(id, interventionId)
      .then((item) => {
        commit('SET_INTERVENTION', item)
        return true
      })
      .catch((error) => {
        commit('SET_INTERVENTION', null)
        alertError(commit, error)
        return false
      })
  },

  saveIntervention: async ({ commit }, { id, payload, interventionId }) => {
    return await service.saveIntervention(id, payload, interventionId)
      .then(() => {
        commit('baseSnackbar/success', { text: 'Actividad guardada con éxito' }, { root: true })
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  getCatalog: async ({ commit, state }) => {
    if (state.catalog) return true
    return await service.getAutonomyCatalog()
      .then((data) => {
        commit('SET_CATALOG', data)
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  getChecklist: async ({ commit }, { checklistId, type }) => {
    return await service.getAutonomyChecklist(checklistId)
      .then((data) => {
        commit(type === 'BIANNUAL' ? 'SET_BIANNUAL_CHECKLIST' : 'SET_MONTHLY_CHECKLIST', data)
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  saveChecklist: async ({ commit }, { checklistId, payload }) => {
    const request = checklistId ? service.updateAutonomyChecklist(checklistId, payload) : service.createAutonomyChecklist(payload)
    return await request
      .then(() => {
        commit('baseSnackbar/success', { text: 'Lista de verificación guardada con éxito' }, { root: true })
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  addChecklistComment: async ({ commit }, { checklistId, payload }) => {
    return await service.addAutonomyChecklistComment(checklistId, payload)
      .then(() => {
        commit('baseSnackbar/success', { text: 'Comentario guardado con éxito' }, { root: true })
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  },

  getAutoIndicators: async ({ commit }, { id, period }) => {
    return await Promise.all([service.getAutoIndicators(id, period), service.getAutonomyCatalog()])
      .then(([auto, catalog]) => {
        commit('SET_AUTO_INDICATORS', auto.indicators.map((indicator) => {
          const question = catalog.questions.find((q) => q.id === indicator.questionId)
          const keyFactor = question && catalog.keyFactors.find((k) => k.id === question.keyFactorId)
          const domain = keyFactor && catalog.domains.find((d) => d.id === keyFactor.domainId)
          return {
            id: indicator.questionId,
            domain: domain ? domain.code : null,
            title: keyFactor ? keyFactor.name : indicator.code,
            description: question ? question.text : '',
            value: indicator.rawValue
          }
        }))
        return true
      })
      .catch((error) => {
        commit('SET_AUTO_INDICATORS', [])
        alertError(commit, error)
        return false
      })
  },

  getTeamActivity: async ({ commit }, { id, from, to }) => {
    return await service.getTeamIndicators(id, from, to)
      .then((data) => {
        commit('SET_TEAM_ACTIVITY', data)
        return true
      })
      .catch((error) => {
        alertError(commit, error)
        return false
      })
  }

}
